import React, { useState, useRef } from 'react'
import {
  AcceptActionType,
  ActionNames,
  Config,
  countAffectedRows,
  CrateAction,
  InAssortmentActionType,
  saveActions,
  stagedIfChanged,
  withoutUndefinedActions,
} from 'domain/Assortment'
import { Dialog } from '../../Dialog'
import { StagedModifications } from '../StagedModifications'
import { CrateActionDialog, CrateActionSnapshot, DialogState, DisabledCrateActions } from './CrateActionDialog'
import {
  CrateActionDialogMultiselect,
  CrateActionSnapshotMultiselect,
  DisabledCrateActionsMultiselect,
} from './CrateActionDialogMulti'
import { Action, EditableActions, FinalStatus, StagedAction } from '../CommonComponents'
import { CrateFile, CrateGroup, Modification } from 'domain/DTO'
import { SortableHeader, useTableSorting } from '../TableColumnSort'
import { paginationOptions } from '../AssortmentDatabase'
import { getPageData, PagedTableControls } from '@tomra/react-table'
import { logError } from '../../../lib/logError'
import { hasWriteInstallationPermission } from '../../../lib/Permission'
import {
  AcceptedValue,
  InAssortmentValue,
  MinimumCrateItemsValue,
  RefundGroupValue,
  SuperGroupValue,
} from './CrateActionElements'
import { EditIcon } from '../../../icons'
import { ModifyAllDialog } from './CrateAllActionDialog'
import {
  acceptActionId,
  allAcceptOrRejectActionId,
  allMinimumCrateItemsActionId,
  calculateFinalStatus,
  createCrateActions,
  increaseBottleToleranceActionId,
  inAssortmentActionId,
  minimumCrateItemsActionId,
  refundGroupActionId,
  superGroupActionId,
} from './CrateTableUtils'

const MULTI_ID = '__MULTI__'

type Props = {
  config: Config
  database: CrateFile
  commonModifications: Modification[]
  installationModifications: Modification[]
  modificationUrl: string
  refreshModifications(): void
}

export const CrateInstallationTable = ({
  config,
  database,
  commonModifications,
  installationModifications,
  modificationUrl,
  refreshModifications,
}: Props) => {
  const [dialogState, setDialogState] = useState({ show: false } as DialogState)
  const [dialogAllState, setDialogAllState] = useState({ show: false } as DialogState)
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPrPage, setRecordsPrPage] = useState(100)
  const [filterGroup, setFilterGroup] = useState('')
  const [filterName, setFilterName] = useState('')
  const [staged, setStaged] = useState<{ [key: string]: CrateAction | undefined }>({})
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const selectedIdsRef = useRef<Set<string>>(new Set())
  const [multiSnapshot, setMultiSnapshot] = useState<CrateActionSnapshotMultiselect | null>(null)
  const [showMultiCheckboxes, setShowMultiCheckboxes] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmLines, setConfirmLines] = useState<string[]>([])

  const common = createCrateActions(commonModifications)
  const override = createCrateActions(installationModifications)

  const sorting = useTableSorting<CrateGroup>(
    {
      'Crate Group': (cg) => cg.id,
      Name: (cg) => cg.name,
      CommonConfig: (cg) => {
        const id = cg.id
        return (
          [
            acceptActionId(id),
            inAssortmentActionId(id),
            refundGroupActionId(id),
            minimumCrateItemsActionId(id),
            superGroupActionId(id),
          ]
            .map((id) => common[id] ?? staged[id])
            .filter((action) => !!action)[0]
            ?.type.toString() ?? ''
        )
      },
      Override: (cg) => {
        const id = cg.id
        return (
          [
            acceptActionId(id),
            inAssortmentActionId(id),
            refundGroupActionId(id),
            minimumCrateItemsActionId(id),
            increaseBottleToleranceActionId(id),
            superGroupActionId(id),
          ]
            .map((id) => override?.[id] ?? staged[id])
            .filter((action) => !!action)[0]
            ?.type.toString() ?? ''
        )
      },
      Accepted: (cg) => {
        return calculateFinalStatus(cg, common, override, staged).toString()
      },
    },
    'Crate Group',
  )

  const rows = database?.crateGroups
    ? sorting.sort(
        Array.from(database.crateGroups)
          .filter((cg) => `crate ${cg.id}`.toLocaleLowerCase().includes(filterGroup.toLocaleLowerCase()))
          .filter((cg) => `${cg.name}`.toLocaleLowerCase().includes(filterName.toLocaleLowerCase())),
      )
    : []

  function handleModifyAllOnClick() {
    return () =>
      setDialogAllState({
        id: 'modifyAll',
        show: true,
        snapshot: {
          allMinimumCrateItemsAction:
            staged[allMinimumCrateItemsActionId()] ?? override[allMinimumCrateItemsActionId()],
          allAcceptOrRejectAction: staged[allAcceptOrRejectActionId()] ?? override[allAcceptOrRejectActionId()],
        },
        disabled: {} as DisabledCrateActions,
      })
  }

  function canOverride(actionId: string) {
    const allow = common[actionId]?.allowOverride
    return typeof allow === 'undefined' ? true : allow
  }

  function stageMultiForIds(ids: string[]) {
    if (!multiSnapshot || ids.length === 0) return
    const accept = multiSnapshot.acceptAction
    const inAssortment = multiSnapshot.inAssortmentAction
    setStaged((prev) => {
      const next = { ...prev }
      for (const id of ids) {
        if (accept && canOverride(acceptActionId(id))) {
          const effective =
            accept.type === AcceptActionType.REMOVE
              ? ({ ...accept, type: 'UNSET' as AcceptActionType } as CrateAction)
              : accept
          next[acceptActionId(id)] = stagedIfChanged(effective, override[acceptActionId(id)])
        }
        if (inAssortment && canOverride(inAssortmentActionId(id))) {
          const effective =
            inAssortment.type === InAssortmentActionType.REMOVE
              ? ({ ...inAssortment, type: 'UNSET' as InAssortmentActionType } as CrateAction)
              : inAssortment
          next[inAssortmentActionId(id)] = stagedIfChanged(effective, override[inAssortmentActionId(id)])
        }
      }
      return next
    })
  }

  function clearMultiForIds(ids: string[]) {
    if (!multiSnapshot || ids.length === 0) return
    const accept = multiSnapshot.acceptAction
    const inAssortment = multiSnapshot.inAssortmentAction
    setStaged((prev) => {
      const next = { ...prev }
      for (const id of ids) {
        if (accept && canOverride(acceptActionId(id))) delete next[acceptActionId(id)]
        if (inAssortment && canOverride(inAssortmentActionId(id))) delete next[inAssortmentActionId(id)]
      }
      return next
    })
  }

  function toggleSelect(id: string) {
    const next = new Set(selectedIdsRef.current)
    const willSelect = !next.has(id)
    if (willSelect) {
      next.add(id)
      stageMultiForIds([id])
    } else {
      next.delete(id)
      clearMultiForIds([id])
    }
    selectedIdsRef.current = next
    setSelectedIds(next)
  }

  function clearSelection() {
    selectedIdsRef.current = new Set()
    setSelectedIds(new Set())
  }

  function selectAllIds(checked: boolean) {
    if (!showMultiCheckboxes) return
    if (checked) {
      const allIds = rows.map((cg) => cg.id.toString())
      const next = new Set(allIds)
      selectedIdsRef.current = next
      setSelectedIds(next)
      stageMultiForIds(allIds)
    } else {
      const current = Array.from(selectedIdsRef.current)
      clearMultiForIds(current)
      clearSelection()
    }
  }

  function resetMulti() {
    if (multiSnapshot) {
      clearMultiForIds(Array.from(selectedIdsRef.current))
    }
    clearSelection()
    setMultiSnapshot(null)
    setShowMultiCheckboxes(false)
  }

  function summarizeStagedChanges(): string[] {
    const added: { [type: string]: number } = {}
    const removed: { [type: string]: number } = {}
    const currentStaged = withoutUndefinedActions(staged)

    Object.entries(currentStaged).forEach(([id, action]) => {
      if (!action) return
      const type = action.type
      if (type === 'UNSET') {
        const prevType = override[id]?.type
        if (prevType && prevType !== 'UNSET') {
          removed[prevType] = (removed[prevType] || 0) + 1
        }
      } else {
        added[type] = (added[type] || 0) + 1
      }
    })

    const parts: string[] = []
    const addedParts = Object.entries(added).map(([t, c]) => `${c} ${ActionNames[t] ?? t}`)
    const removedParts = Object.entries(removed).map(([t, c]) => `${c} ${ActionNames[t] ?? t}`)
    if (addedParts.length) parts.push(`set ${addedParts.join(', ')}`)
    if (removedParts.length) parts.push(`remove ${removedParts.join(', ')}`)

    if (!parts.length) return ['No staged changes to apply.']
    return [`You are about to ${parts.join(', ')}.`]
  }

  async function onApplyClick(): Promise<void> {
    const isMultiActive = !!multiSnapshot || showMultiCheckboxes
    if (isMultiActive) {
      const summary = summarizeStagedChanges()
      setConfirmLines(summary)
      setConfirmOpen(true)
    } else {
      await applyStaged()
    }
  }

  return (
    <div>
      <div className="my-md pl-lg">
        {hasWriteInstallationPermission() && (
          <StagedModifications numberOfStaged={getNumberOfStaged()} onClear={resetStaged} onApply={onApplyClick} />
        )}
      </div>
      <div className="flex items-center pl-lg py-sm">
        <div className="inline-block items-center">
          <label>
            <span>Crate Group:</span>
            <input
              type="text"
              className="mx-1"
              style={{ width: '60%' }} // overrides input[type="text"] {width: 100%}
              data-testid="search-bottle-group"
              onChange={(e) => {
                setFilterGroup(e.target.value.toLocaleLowerCase())
                setCurrentPage(1)
              }}
            />
          </label>
          <span
            className="font-bold cursor-pointer"
            title={'You can search for crate groups explicitly like: `crate 1010`'}
          >
            ?
          </span>
        </div>
        <div className="inline-block items-center ml-1">
          <label>
            <span>Name:</span>
            <input
              type="text"
              className="mx-1"
              style={{ width: '60%' }} // overrides input[type="text"] {width: 100%}
              data-testid="search-name"
              onChange={(e) => {
                setFilterName(e.target.value.toLocaleLowerCase())
                setCurrentPage(1)
              }}
            />
          </label>
        </div>
        {hasWriteInstallationPermission() && (
          <div className="flex flex-1 justify-end mr-5">
            <div className="flex flex-col items-end space-y-2">
              {multiSnapshot ? (
                <div className="border-4 p-1" style={{ borderColor: '#0d7dc2', minWidth: 250 }}>
                  <span className="font-bold" style={{ color: '#0d7dc2' }}>
                    Multiselect mode active
                  </span>
                  <div className="mb-2">
                    <span className="font-bold">Multiselect action: </span>
                    <EditableActions onClick={openMultiDialog}>
                      {multiSnapshot.acceptAction && (
                        <p className="text-xs opacity-70">Accepted: {multiSnapshot.acceptAction.type}</p>
                      )}
                      {multiSnapshot.inAssortmentAction && (
                        <p className="text-xs opacity-70">In assortment: {multiSnapshot.inAssortmentAction.type}</p>
                      )}
                    </EditableActions>
                  </div>
                  <button className="btn btn-sm mt-2 w-full" onClick={resetMulti} type="button">
                    Cancel and exit multiselect mode
                  </button>
                </div>
              ) : (
                <div className="flex items-center">
                  <span className="mr-1">Start multiselect mode:</span>
                  <EditableActions onClick={openMultiDialog} />
                </div>
              )}

              <button className="btn" onClick={handleModifyAllOnClick()} type="button">
                <span className="flex items-center justify-between w-full">
                  <span className="mr-1">Modify all</span>
                  <EditIcon color="none" />
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      <table className="table table-fixed mt-1">
        <thead>
          <tr>
            <SortableHeader name="Crate Group" currentSort={sorting.currentSort} />
            <SortableHeader name="Name" currentSort={sorting.currentSort} />
            <th>Database</th>
            <th>Common Modification All</th>
            <SortableHeader name="CommonConfig" currentSort={sorting.currentSort} />
            <th>Override Modification All</th>
            <SortableHeader name="Override" currentSort={sorting.currentSort} />
            <SortableHeader name="Accepted" currentSort={sorting.currentSort} />
            {showMultiCheckboxes && (
              <th>
                Select all
                <input
                  type="checkbox"
                  className="checkbox align-middle ml-sm"
                  onChange={(e) => selectAllIds(e.target.checked)}
                  checked={selectedIds.size === rows.length && rows.length > 0}
                />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {getPageData(rows, currentPage, recordsPrPage).map((crateGroup) => {
            const id = crateGroup.id.toString()
            return (
              <tr key={id} className="even:bg-grey-light hover:bg-fog">
                <td>{id}</td>
                <td>{crateGroup.name}</td>
                <td>
                  <AcceptedValue accepted={crateGroup.accepted} />
                  <InAssortmentValue inAssortment={crateGroup.inAssortment} />
                  <RefundGroupValue refund={crateGroup.refundGroup} />
                  <MinimumCrateItemsValue minimumCrateItems={crateGroup.minimumCrateItems} />
                  <SuperGroupValue superGroup={crateGroup.superGroup} />
                </td>
                <td>
                  <Action key={allMinimumCrateItemsActionId()} action={common[allMinimumCrateItemsActionId()]} />
                  <Action key={allAcceptOrRejectActionId()} action={common[allAcceptOrRejectActionId()]} />
                </td>
                <td>
                  {[
                    acceptActionId(id),
                    inAssortmentActionId(id),
                    refundGroupActionId(id),
                    minimumCrateItemsActionId(id),
                    increaseBottleToleranceActionId(id),
                    superGroupActionId(id),
                  ]
                    .filter((id) => common[id])
                    .map((id) => (
                      <Action key={id} action={common[id]} />
                    ))}
                </td>
                <td>
                  <StagedAction
                    original={override[allMinimumCrateItemsActionId()]}
                    staged={staged[allMinimumCrateItemsActionId()]}
                  />
                  <StagedAction
                    original={override[allAcceptOrRejectActionId()]}
                    staged={staged[allAcceptOrRejectActionId()]}
                  />
                </td>
                <td>
                  {hasWriteInstallationPermission() && (
                    <EditableActions onClick={() => openDialog(id)}>
                      {[
                        acceptActionId(id),
                        inAssortmentActionId(id),
                        refundGroupActionId(id),
                        minimumCrateItemsActionId(id),
                        increaseBottleToleranceActionId(id),
                        superGroupActionId(id),
                      ]
                        .filter((id) => override[id] || staged[id])
                        .map((id) => (
                          <StagedAction key={id} original={override[id]} staged={staged[id]} />
                        ))}
                    </EditableActions>
                  )}
                </td>
                <td>
                  <FinalStatus ok={calculateFinalStatus(crateGroup, common, override, staged)} />
                </td>
                {showMultiCheckboxes && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleSelect(id)}
                      aria-label={`select ${id}`}
                    />
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
      <PagedTableControls
        currentPage={currentPage}
        numOfItems={rows.length}
        recordsPerPage={recordsPrPage}
        onPageUpdate={setCurrentPage}
        recordsPerPageOptions={paginationOptions}
        onRecordsPerPageChange={(recordsPerPage: number) => {
          setRecordsPrPage(recordsPerPage)
          setCurrentPage(1)
        }}
      />
      {dialogState.show && dialogState.id ? (
        <Dialog onClose={() => setDialogState({ ...dialogState, show: false })} shouldCloseOnOverlayClick={false}>
          {dialogState.id === MULTI_ID ? (
            <CrateActionDialogMultiselect
              config={config}
              id={dialogState.id}
              snapshot={(dialogState.snapshot as unknown as CrateActionSnapshotMultiselect) ?? {}}
              disabled={
                (dialogState.disabled as unknown as DisabledCrateActionsMultiselect) ?? {
                  acceptAction: false,
                  inAssortmentAction: false,
                }
              }
              onApply={saveToStagedMulti}
            />
          ) : (
            <CrateActionDialog
              config={config}
              id={dialogState.id}
              snapshot={dialogState.snapshot}
              disabled={dialogState.disabled}
              onApply={(id, snapshot) => {
                onUserEditedActions(id, snapshot)
                setDialogState({ ...dialogState, show: false })
              }}
            />
          )}
        </Dialog>
      ) : null}
      {dialogAllState.show && (
        <Dialog onClose={() => setDialogAllState({ ...dialogAllState, show: false })} shouldCloseOnOverlayClick={false}>
          <ModifyAllDialog
            config={config}
            id={'all'}
            snapshot={{
              allMinimumCrateItemsAction:
                staged[allMinimumCrateItemsActionId()] ?? override[allMinimumCrateItemsActionId()],
              allAcceptOrRejectAction: staged[allAcceptOrRejectActionId()] ?? override[allAcceptOrRejectActionId()],
            }}
            disabled={{
              acceptAction: false,
              inAssortmentAction: false,
              refundGroupAction: false,
              minimumCrateItemsAction: false,
              increaseBottleToleranceAction: false,
              allMinimumCrateItemsAction: false,
              allAcceptOrRejectAction: false,
              superGroupAction: false,
            }}
            onApply={(id: string, snapshot: CrateActionSnapshot) => {
              onUserEditedActions(id, snapshot)
              setDialogAllState({ ...dialogAllState, show: false })
            }}
          />
        </Dialog>
      )}
      {confirmOpen && (
        <Dialog onClose={() => setConfirmOpen(false)} shouldCloseOnOverlayClick={false}>
          <div className="card">
            <div className="p-md">
              <div className="mb-sm font-bold text-lg">Confirm apply</div>
              <div className="mb-md">
                {confirmLines.map((l, i) => (
                  <p key={i}>{l}</p>
                ))}
              </div>
              <div className="flex justify-end space-x-2">
                <button className="btn btn-secondary" type="button" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={async () => {
                    await applyStaged()
                    setConfirmOpen(false)
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  )

  function getNumberOfStaged() {
    return staged[allMinimumCrateItemsActionId()] || staged[allAcceptOrRejectActionId()]
      ? rows.length
      : countAffectedRows(Object.keys(withoutUndefinedActions(staged)))
  }

  function resetStaged() {
    clearSelection()
    resetMulti()
    setStaged({})
  }

  async function applyStaged() {
    try {
      await saveActions('CRATE', modificationUrl, override, staged)
      clearSelection()
      resetMulti()
      setStaged({})
      refreshModifications()
    } catch (error: unknown) {
      logError(error)
    }
  }

  function onUserEditedActions(id: string, snapshot: CrateActionSnapshot) {
    setStaged({
      ...staged,
      [acceptActionId(id)]: stagedIfChanged(snapshot.acceptAction, override[acceptActionId(id)]),
      [inAssortmentActionId(id)]: stagedIfChanged(snapshot.inAssortmentAction, override[inAssortmentActionId(id)]),
      [refundGroupActionId(id)]: stagedIfChanged(snapshot.refundGroupAction, override[refundGroupActionId(id)]),
      [minimumCrateItemsActionId(id)]: stagedIfChanged(
        snapshot.minimumCrateItemsAction,
        override[minimumCrateItemsActionId(id)],
      ),
      [increaseBottleToleranceActionId(id)]: stagedIfChanged(
        snapshot.increaseBottleToleranceAction,
        override[increaseBottleToleranceActionId(id)],
      ),
      [allMinimumCrateItemsActionId()]: stagedIfChanged(
        snapshot.allMinimumCrateItemsAction,
        override[allMinimumCrateItemsActionId()],
      ),
      [allAcceptOrRejectActionId()]: stagedIfChanged(
        snapshot.allAcceptOrRejectAction,
        override[allAcceptOrRejectActionId()],
      ),
      [superGroupActionId(id)]: stagedIfChanged(snapshot.superGroupAction, override[superGroupActionId(id)]),
    })
  }

  function saveToStagedMulti(id: string, snapshot: CrateActionSnapshotMultiselect) {
    setMultiSnapshot(snapshot)
    setShowMultiCheckboxes(true)
    stageMultiForIds(Array.from(selectedIdsRef.current))
    setDialogState((ds) => ({ ...ds, show: false }))
  }

  function openMultiDialog() {
    setDialogState({
      show: true,
      id: MULTI_ID,
      snapshot:
        (multiSnapshot as unknown as CrateActionSnapshot) ??
        ({
          acceptAction: undefined,
          inAssortmentAction: undefined,
        } as unknown as CrateActionSnapshot),
      disabled: {
        acceptAction: false,
        inAssortmentAction: false,
        increaseBottleToleranceAction: false,
      } as unknown as DisabledCrateActions,
    })
  }

  function openDialog(id: string) {
    setDialogState({
      show: true,
      id: id,
      snapshot: {
        acceptAction: staged[acceptActionId(id)] ?? override[acceptActionId(id)],
        inAssortmentAction: staged[inAssortmentActionId(id)] ?? override[inAssortmentActionId(id)],
        refundGroupAction: staged[refundGroupActionId(id)] ?? override[refundGroupActionId(id)],
        minimumCrateItemsAction: staged[minimumCrateItemsActionId(id)] ?? override[minimumCrateItemsActionId(id)],
        increaseBottleToleranceAction:
          staged[increaseBottleToleranceActionId(id)] ?? override[increaseBottleToleranceActionId(id)],
        allMinimumCrateItemsAction: staged[allMinimumCrateItemsActionId()] ?? override[allMinimumCrateItemsActionId()],
        allAcceptOrRejectAction: staged[allAcceptOrRejectActionId()] ?? override[allAcceptOrRejectActionId()],
        superGroupAction: staged[superGroupActionId(id)] ?? override[superGroupActionId(id)],
      },
      disabled: {
        acceptAction:
          typeof common[acceptActionId(id)]?.allowOverride !== 'undefined' &&
          !common[acceptActionId(id)]?.allowOverride,
        inAssortmentAction:
          typeof common[inAssortmentActionId(id)]?.allowOverride !== 'undefined' &&
          !common[inAssortmentActionId(id)]?.allowOverride,
        refundGroupAction:
          typeof common[refundGroupActionId(id)]?.allowOverride !== 'undefined' &&
          !common[refundGroupActionId(id)]?.allowOverride,
        minimumCrateItemsAction:
          typeof common[minimumCrateItemsActionId(id)]?.allowOverride !== 'undefined' &&
          !common[minimumCrateItemsActionId(id)]?.allowOverride,
        increaseBottleToleranceAction: false,
        allMinimumCrateItemsAction:
          typeof common[allMinimumCrateItemsActionId()]?.allowOverride !== 'undefined' &&
          !common[allMinimumCrateItemsActionId()]?.allowOverride,
        allAcceptOrRejectAction:
          typeof common[allAcceptOrRejectActionId()]?.allowOverride !== 'undefined' &&
          !common[allAcceptOrRejectActionId()]?.allowOverride,
        superGroupAction:
          typeof common[superGroupActionId(id)]?.allowOverride !== 'undefined' &&
          !common[superGroupActionId(id)]?.allowOverride,
      },
    })
  }
}
